import makeStyles from '@mui/styles/makeStyles';
import {getScreenDetail, getScreenIndex, getTimeRemaining, timeRemainingMessage} from "business/matching";
import {isMentee, isMentor} from "business/role";
import ConfirmSlotsDialog from "components/Matching/ConfirmSlotsDialog";
import MatchingScreenIntro, {useMatchingCopy} from "components/Matching/MatchingScreenIntro";
import CongratulationsWindow from "components/CongratulationsWindow";
import MatchingHistoryModal from "components/MatchingHistoryModal";
import Skeleton from "@mui/material/Skeleton";
import {History} from "lucide-react";
import {get, isEmpty} from "lodash";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import AwaitingResponse from "views/Matching/AwaitingResponse";
import GroupHold from "views/Matching/GroupHold";
import MenteeMatchSubmitButton from "views/Matching/MenteeMatchSubmitButton";
import MentorMatchSubmitButton from "views/Matching/MentorMatchSubmitButton";
import PotentialMatches from "views/Matching/PotentialMatches";
import ProfileSearchArt from "components/Illustrations/ProfileSearchArt";
import CtaButton from "components/redesign/CtaButton";
import {color, type} from "common/tokens";

// Sizes come from the Figma frames: an 824px page column, a 187x34 history
// button beside the heading, and a 240x210 illustration set against the
// finding-matches copy rather than under it.
const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 24,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: 16,
    },
  },
  heading: {
    minWidth: 0,
  },
  historyButton: {
    fontSize: 11,
    padding: '6px 14px',
  },
  // Copy starts level with the illustration rather than centring against it,
  // which left it floating well below the heading.
  findingBody: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 56,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: 24,
    },
  },
  findingCopy: {
    maxWidth: 400,
    ...type.body,
    color: color.TEXT_MUTED,
  },
  findingArt: {
    display: 'block',
    width: 240,
    flexShrink: 0,
    marginTop: 24,
  },
  footer: {
    display: 'flex',
    justifyContent: 'center',
  },
  skeleton: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
}));

const LoadingSkeleton = () => {
  const classes = useStyles();

  return (
    <div className={classes.skeleton}>
      <Skeleton animation={'wave'} height={40} width={'60%'}/>
      <Skeleton animation={'wave'} variant="rectangular" height={120}/>
      <Skeleton animation={'wave'} variant="rectangular" height={120}/>
    </div>
  );
};

const MatchingContentWrapper = ({
  userMatchType,
  groupInfo,
  expiryHours,
  activeMatches,
  submitMatchesResponse,
  screen,
  screensList,
  submitMatch,
  role,
  updateMatch,
  matches,
  getMatchesList,
  setCongratulationsState,
  showGroupHoldCongratulationDialog,
  timeSlotOptions,
  clearTimeSlotOptions,
}) => {
  const classes = useStyles();
  const {t} = useTranslation();

  const [historyOpen, setHistoryOpen] = useState(false);

  const screenindex = getScreenIndex(screen);
  const isGroupHold = screenindex === 4;
  const isFinding = screenindex !== 2 && screenindex !== 3 && !isGroupHold;

  const findingCopy = useMatchingCopy(getScreenDetail(screensList, 'finding_matches'));

  const intro = () => {
    if (isGroupHold) {
      return {
        listDetail: getScreenDetail(screensList, 'group_hold'),
        fallbackTitle: t('groupHold'),
        countdown: `${t('beginsIn')} ${timeRemainingMessage(
          getTimeRemaining(get(groupInfo, 'createdOn'), get(groupInfo, 'groupStartsIn')))}`,
      };
    }
    if (screenindex === 3) {
      return {listDetail: getScreenDetail(screensList, 'awaiting_mentor_response'), fallbackTitle: t('waitingToHearBack')};
    }
    if (screenindex === 2) {
      return {listDetail: getScreenDetail(screensList, 'potential_match'), fallbackTitle: t('matchesForYou')};
    }
    return {listDetail: getScreenDetail(screensList, 'finding_matches'), fallbackTitle: t('findingMatchesForYou')};
  };

  const body = () => {
    switch (screenindex) {
      case 2:
        return <PotentialMatches
          matches={activeMatches}
          onUpdateMatch={updateMatch}
          role={role}
          expiryHours={expiryHours}
          groupInfo={groupInfo}
          userMatchType={userMatchType}
          submitMatchesResponse={submitMatchesResponse}/>;
      case 3:
        return <AwaitingResponse matches={activeMatches}/>;
      case 4:
        return <GroupHold groupInfo={groupInfo}/>;
      default:
        return null;
    }
  };

  if (isEmpty(screensList)) {
    return <LoadingSkeleton/>;
  }

  const {listDetail, fallbackTitle, countdown} = intro();

  return (
    <>
      <div className={classes.header}>
        <div className={classes.heading}>
          <MatchingScreenIntro listDetail={listDetail} fallbackTitle={fallbackTitle} countdown={countdown}
                               showDescription={isFinding ? 'none' : 'link'}/>
        </div>
        <CtaButton
          variant={'secondary'}
          tone={'info'}
          size={'dense'}
          label={t('viewMatchingHistory')}
          icon={<History size={13} strokeWidth={2.25}/>}
          className={classes.historyButton}
          onClick={() => setHistoryOpen(true)}/>
      </div>

      {isFinding
        ? (
          <div className={classes.findingBody}>
            <div className={classes.findingCopy}>{findingCopy.description}</div>
            <ProfileSearchArt className={classes.findingArt}/>
          </div>
        )
        : body()}

      {screenindex === 2 && (
        <div className={classes.footer}>
          {isMentee(role) && <MenteeMatchSubmitButton matches={activeMatches} submit={() => submitMatch(activeMatches)}/>}
          {isMentor(role) && <MentorMatchSubmitButton matches={activeMatches} submit={() => submitMatch(activeMatches, true)}/>}
        </div>
      )}

      {!isEmpty(timeSlotOptions) && (
        <ConfirmSlotsDialog
          slots={timeSlotOptions}
          onClose={clearTimeSlotOptions}
          onConfirm={timeSlots => submitMatch(activeMatches, true, timeSlots)}/>
      )}

      <MatchingHistoryModal open={historyOpen} handleState={() => setHistoryOpen(false)}
                            getMatchesList={getMatchesList} matches={matches}/>

      {/* Landing on group hold is the whole of the celebration here — a formed
          mentorship closes matching outright, so it is announced on the
          mentorship home instead. */}
      <CongratulationsWindow open={isGroupHold && showGroupHoldCongratulationDialog}
                             handleClose={() => setCongratulationsState(false)}/>
    </>
  )
};

export default MatchingContentWrapper;
